<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

import { SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';

const { tt } = useTypedI18n();

const wrapperRef = ref<HTMLElement | null>(null);

const search = useHeaderSearch();

onClickOutside(wrapperRef, () => search.closeDropdown());

const auctionItems = computed(() =>
    search.auctions.value.map(item => ({
        id: item.id,
        slug: item.slug,
        vin: item.mainAsset.vin,
        brand: item.mainAsset.brand,
        model: item.mainAsset.model,
        coverImage: item.mainAsset.coverImage,
        price: item.currentPrice,
    }))
);
const directSaleItems = computed(() =>
    search.directSales.value.map(item => ({
        id: item.id,
        slug: item.slug,
        vin: item.mainAsset.vin,
        brand: item.mainAsset.brand,
        model: item.mainAsset.model,
        coverImage: item.mainAsset.coverImage,
        price: item.buyNowPrice,
    }))
);
</script>

<template>
    <UPopover
        class="w-full"
        mode="click"
        :content="{
            align: 'start',
            side: 'left',
            sideOffset: -40,
        }"
    >
        <UButton icon="lucide:search" size="lg" variant="ghost" color="neutral" :aria-label="tt('search.aria.openSearch')" />

        <template #content>
            <UInput
                ref="wrapperRef"
                v-model="search.inputValue.value"
                trailing-icon="lucide:search"
                autocomplete="off"
                :placeholder="tt('search.placeholder')"
                class="w-sm"
                size="lg"
                @keydown.enter.prevent="search.onEnter()"
                @keydown.down.prevent="search.onArrowDown()"
                @keydown.up.prevent="search.onArrowUp()"
                @keydown.escape="search.onEscape()"
            >
                <template #trailing>
                    <UButton v-if="search.inputValue.value" icon="lucide:x" size="xs" square variant="ghost" @click="search.onClear()" />
                </template>
            </UInput>

            <div
                v-if="search.isOpen.value"
                class="absolute top-full right-0 left-0 z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-lg border border-neutral-200 bg-white p-2 text-sm shadow-lg"
            >
                <div v-if="search.isFetching.value" class="flex justify-center py-4">
                    <UIcon name="lucide:loader-circle" class="animate-spin text-xl text-neutral-400" />
                </div>

                <div v-else-if="!search.hasResults.value" class="py-4 text-center text-neutral-500">
                    {{ tt('search.noResults', { q: search.debouncedValue.value.trim() }) }}
                </div>

                <template v-else>
                    <HeaderSearchResultSection
                        class="text-default mb-3"
                        title-key="search.section.auctions"
                        view-all-key="search.viewAll.auctions"
                        :items="auctionItems"
                        :total="search.auctionsTotal.value"
                        :route-name="ROUTE_NAMES.AUCTION_DETAIL"
                        :view-all-route="search.getViewAllRoute(SellingMethod.Auction, search.debouncedValue.value)"
                        :get-highlight-class="search.getResultHighlightClass"
                        @item-click="search.onResultClick()"
                    />
                    <HeaderSearchResultSection
                        class="text-default"
                        title-key="search.section.buyNow"
                        view-all-key="search.viewAll.buyNow"
                        :items="directSaleItems"
                        :total="search.directSalesTotal.value"
                        :route-name="ROUTE_NAMES.DIRECT_SALE_DETAIL"
                        :view-all-route="search.getViewAllRoute(SellingMethod.DirectSale, search.debouncedValue.value)"
                        :highlight-offset="search.auctions.value.length"
                        :get-highlight-class="search.getResultHighlightClass"
                        @item-click="search.onResultClick()"
                    />
                </template>
            </div>
        </template>
    </UPopover>
</template>
